import { RateOwnerType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { RateBasicWithValue } from '~hooks/useRates/models';

interface SelectRateCardDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (rate: RateBasicWithValue | null) => void;
  fetchRates: (args?: { query: string; cursor: string }) => void;
  rateOwnerType: RateOwnerType;
}

interface SelectRateCardFormData {
  targetRate: RateBasicWithValue | null;
}

export const SelectRateCardDialog = ({
  open,
  onClose,
  onSelect,
  rateOwnerType,
  fetchRates,
}: SelectRateCardDialogProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SelectRateCardFormData>({
    defaultValues: {
      targetRate: null,
    },
  });

  useEffect(() => {
    if (open) {
      reset({ targetRate: null });
    }
  }, [open, rateOwnerType, reset]);

  const onSubmit = (data: SelectRateCardFormData) => {
    onSelect(data.targetRate);
    onClose();
  };

  const getRateLabel = () => {
    if (rateOwnerType === RateOwnerType.VENDOR) {
      return t('order.form.vendor_rate_card');
    } else if (rateOwnerType === RateOwnerType.DRIVER) {
      return t('order.form.driver_rate_card');
    } else if (rateOwnerType === RateOwnerType.CUSTOMER) {
      return t('order.form.customer_rate_card');
    }
  };

  return (
    <Dialog isOpen={open} onClose={onClose} maxWidth="sm">
      <DialogHeader onClose={onClose}>{t('form_fields.select_rate_card')}</DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <AutocompleteAsyncFormField
            asyncCallback={(args: { query: string; cursor: string }) => fetchRates(args)}
            clearable
            control={control}
            disablePortal={false}
            errors={errors}
            getLabel={(item) => item.name || ''}
            getValue={(item) => item.id}
            label={getRateLabel()}
            name="targetRate"
          />
        </DialogContent>

        <DialogActions>
          <SmallButton onClick={onClose} variant="outlined" color="secondary">
            {t('actions.cancel')}
          </SmallButton>

          <SmallButton type="submit">{t('actions.done')}</SmallButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
